import { ScrollToTop } from "apps/visits/core/components/scroll-to-top";
import { useTrackPageViews } from "apps/visits/core/modules/tracking/gtm";
import { goToUrl } from "apps/visits/core/utils/go-to-url";
import { sortBy } from "lodash";
import { useEffect } from "react";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import { Footer } from "@/core/shared/v2/footer";
import { Header } from "@/core/shared/v2/header";
import { routes } from "../config/routes";
import { useExpireLSState } from "../hooks/use-expire-ls-state";
import type { Config } from "../models";
import { useConfigStore } from "../store/use-config";
import { useOrderingStore } from "../store/use-ordering-store";
import { Catalog } from "./catalog";
import { FinishedAction } from "./finished-action";
import { Homepage } from "./homepage";
import { LimitReached } from "./limit-reached";
import { Login } from "./login";
import { PhoneVerification } from "./phone-verification";
import { PhoneVerificationError } from "./phone-verification-error";
import { Registration } from "./registration";
import { RunOutOfSlots } from "./run-out-of-slots";
import { SelectTimeSlot } from "./select-time-slot";
import { SimplifyRegistration } from "./simplify-registration";
import { Summary } from "./summary";
import { ThankYou } from "./thank-you";
import { Upsell } from "./upsell";

declare const gon: Config;

export const GroupActionsOrdering = () => {
  const { config, setConfig } = useConfigStore();
  const { expireTime } = useOrderingStore();

  useExpireLSState(expireTime);
  useEffect(() => {
    gon.bloodTestPackages = sortBy(gon.bloodTestPackages, ["position"]);
    setConfig(gon);
  }, [setConfig]);

  if (!config) return null;

  return (
    <Router basename={config?.groupActionPath}>
      <Content config={config} />
    </Router>
  );
};

// Inside Router so useTrackPageViews can rely on react-router's location for SPA nav.
const Content = ({ config }: { config: Config }) => {
  useTrackPageViews({ additionalData: { company_name: config.slug } });

  return (
    // pb-[180px] reserves clearance below the footer for the fixed mobile SideCart sheet (shown below lg); see components/cart/side-cart.tsx
    <div className="flex min-h-screen flex-col bg-primary-foreground pb-[180px] lg:pb-0">
      <Header contractorLogoUrl={config?.contractorLogoUrl} onLogoClick={() => goToUrl(config?.groupActionPath)} />
      <div className="min-h-0 flex-1">
        <ScrollToTop />
        <Switch>
          <Route path={routes.limitReached.raw()} component={LimitReached} exact />
          <Route path={routes.finishedAction.raw()} component={FinishedAction} exact />
          <Route path={routes.runOutOfSlots.raw()} component={RunOutOfSlots} exact />
          <Route path={routes.homepage.raw()} component={Homepage} exact />
          <Route path={routes.catalog.raw()} component={Catalog} exact />
          {config.withCalendar && <Route path={routes.selectTimeSlot.raw()} component={SelectTimeSlot} exact />}
          <Route path={routes.upsell.raw()} component={Upsell} exact />
          <Route path={routes.phoneVerification.raw()} component={PhoneVerification} exact />
          <Route path={routes.login.raw()} component={Login} exact />
          <Route path={routes.phoneVerificationError.raw()} component={PhoneVerificationError} exact />
          <Route path={routes.simplifyRegistration.raw()} component={SimplifyRegistration} exact />
          <Route path={routes.registration.raw()} component={Registration} exact />
          <Route path={routes.summary.raw()} component={Summary} exact />
          <Route path={routes.thankYou.raw()} component={ThankYou} exact />
        </Switch>
      </div>
      <Footer />
    </div>
  );
};
